import {
  Truck,
  Clock,
  DollarSign,
  Shield,
  Wifi,
  MapPin,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Activity,
  Users,
  AlertTriangle,
  Settings,
  Zap,
  ChevronRight,
  Wrench,
  Thermometer,
  Radio,
  Tag,
} from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/80 glass border-b border-gray-100 sticky top-0 z-50 shadow-soft">
        <div className="max-w-7xl mx-auto px-6 py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-2 rounded-xl shadow-md">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">Foreman</h1>
                <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Equipment Timecard System</p>
              </div>
            </div>
            <a
              href="/demo.html"
              className="group inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl shadow-md hover:shadow-glow-primary hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
            >
              View Live Demo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-6 overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/60 via-white to-earth-50/40" />
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-40" />

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/80 text-primary-800 rounded-full text-sm font-semibold mb-6 border border-primary-200/60">
                <Zap className="w-4 h-4" />
                IoT-Powered Construction Automation
              </div>
            </div>
            <h2 className="animate-fade-in-up delay-100 text-5xl md:text-6xl font-extrabold mb-6 tracking-tight text-balance leading-[1.1]">
              <span className="text-gray-900">Stop Losing </span>
              <span className="bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">$40,000/Year</span>
              <span className="text-gray-900"> to Manual Timecards</span>
            </h2>
            <p className="animate-fade-in-up delay-200 text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto">
              Automated IoT-based equipment tracking and payroll system that eliminates time theft,
              disputed hours, and billing errors for construction companies.
            </p>
            <div className="animate-fade-in-up delay-300 flex gap-4 justify-center flex-wrap">
              <a
                href="/demo.html"
                className="group inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-lg font-semibold rounded-2xl shadow-large hover:shadow-glow-primary hover:from-primary-700 hover:to-primary-800 transition-all duration-300 hover:-translate-y-0.5"
              >
                <Activity className="w-5 h-5" />
                See It In Action
                <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-gray-700 text-lg font-semibold rounded-2xl shadow-medium hover:shadow-large border border-gray-200 hover:border-gray-300 transition-all duration-300 hover:-translate-y-0.5"
              >
                Learn More
              </a>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            <div className="animate-fade-in-up delay-300 group bg-white rounded-2xl p-8 border border-success-200/80 text-center shadow-soft hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="text-4xl md:text-5xl font-extrabold text-success-600 mb-1 tracking-tight">$40K+</div>
              <div className="text-gray-900 font-semibold mb-1">Annual Savings</div>
              <div className="text-sm text-gray-500">Per 4-equipment operation</div>
            </div>
            <div className="animate-fade-in-up delay-400 group bg-white rounded-2xl p-8 border border-primary-200/80 text-center shadow-soft hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="text-4xl md:text-5xl font-extrabold text-primary-600 mb-1 tracking-tight">19x</div>
              <div className="text-gray-900 font-semibold mb-1">First-Year ROI</div>
              <div className="text-sm text-gray-500">Pays for itself in weeks</div>
            </div>
            <div className="animate-fade-in-up delay-500 group bg-white rounded-2xl p-8 border border-purple-200/80 text-center shadow-soft hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="text-4xl md:text-5xl font-extrabold text-purple-600 mb-1 tracking-tight">15 min</div>
              <div className="text-gray-900 font-semibold mb-1">Billing Accuracy</div>
              <div className="text-sm text-gray-500">GPS + accelerometer tracking</div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Use This Demo */}
      <section className="py-20 px-6 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
        <div className="relative max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl p-10 md:p-12 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/80 text-primary-800 rounded-full font-semibold text-sm mb-5 border border-primary-200/60">
                <Activity className="w-4 h-4" />
                Interactive Demo
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">How to Use This Demo</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Click "View Live Demo" to see a working dashboard with simulated construction site data
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              <div className="group bg-gradient-to-br from-primary-50 to-primary-100/60 rounded-2xl p-6 border border-primary-200/60 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-primary-600 to-primary-700 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-md">
                    1
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1.5 text-gray-900">Click "View Live Demo"</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      The orange button at the top-right (or any "See It In Action" button) opens the
                      real-time dashboard.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-success-50 to-success-100/60 rounded-2xl p-6 border border-success-200/60 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-success-600 to-success-700 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-md">
                    2
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1.5 text-gray-900">Explore the Dashboard</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      See equipment status, active timecards, billing calculations, and certification
                      alerts — all updating in real-time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-purple-50 to-purple-100/60 rounded-2xl p-6 border border-purple-200/60 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-md">
                    3
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1.5 text-gray-900">Check the Data</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      The demo shows equipment tracking, employees clocked in/out, pay calculations,
                      and daily billing totals.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-blue-50 to-blue-100/60 rounded-2xl p-6 border border-blue-200/60 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-md">
                    4
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1.5 text-gray-900">Click Back Arrow to Return</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Use the arrow button at the top-left of the dashboard to come back to this page
                      and continue reading.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200/80">
              <h4 className="font-bold text-lg mb-4 text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-success-600" />
                What You'll See in the Dashboard
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
                  <span><strong className="text-gray-900">Equipment Status:</strong> Live machine tracking with sensor data</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-success-500 rounded-full flex-shrink-0"></div>
                  <span><strong className="text-gray-900">Active Timecards:</strong> Who's working on what equipment right now</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
                  <span><strong className="text-gray-900">Pay Calculations:</strong> Real-time dual-rate billing (base + equipment multiplier)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-warning-500 rounded-full flex-shrink-0"></div>
                  <span><strong className="text-gray-900">Certification Alerts:</strong> Expiring operator certifications</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                  <span><strong className="text-gray-900">Project Billing:</strong> Per-project cost tracking and invoicing</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span><strong className="text-gray-900">Real-time Updates:</strong> Dashboard refreshes every 30 seconds</span>
                </div>
              </div>
            </div>

            <div className="mt-10 text-center">
              <a
                href="/demo.html"
                className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xl font-bold rounded-2xl shadow-large hover:shadow-glow-primary hover:from-primary-700 hover:to-primary-800 transition-all duration-300 hover:-translate-y-0.5"
              >
                <Activity className="w-6 h-6" />
                Open Dashboard Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <p className="text-sm text-gray-400 mt-4">No login required</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-danger-100/80 text-danger-800 rounded-full text-sm font-semibold mb-5 border border-danger-200/60">
              <AlertTriangle className="w-4 h-4" />
              The Problem
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">The Hidden Cost of Manual Timecards</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">For a typical contractor with 4 machines and 10 operators — here's what you're losing</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            <div className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-soft hover:shadow-large transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-start gap-4">
                <div className="bg-danger-100 p-2.5 rounded-xl flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-danger-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg mb-2 text-gray-900">Time Theft & Rounding Errors</h4>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    Paper timecards are rounded up, buddy-punched, or simply filled in from memory at the
                    end of the day. It adds up fast.
                  </p>
                  <div className="bg-danger-50/80 rounded-xl p-4 text-sm border border-danger-100">
                    <p className="font-semibold text-danger-800 mb-2 text-xs uppercase tracking-wider">The Math</p>
                    <p className="text-gray-700 leading-relaxed">
                      10 operators x $28/hr avg wage<br/>
                      5-8% overpayment on manual timecards<br/>
                      20,800 hrs/year x 5% x $28 = <strong className="text-danger-700">$29,120/year</strong><br/>
                      Conservative estimate: <strong className="text-danger-700">$20,000/year</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-soft hover:shadow-large transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-start gap-4">
                <div className="bg-purple-100 p-2.5 rounded-xl flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg mb-2 text-gray-900">Equipment Billing Errors</h4>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    Without accurate idle vs. active tracking, you're billing clients for
                    full hours when the machine sat idle — or worse, underbilling active time.
                  </p>
                  <div className="bg-purple-50/80 rounded-xl p-4 text-sm border border-purple-100">
                    <p className="font-semibold text-purple-800 mb-2 text-xs uppercase tracking-wider">Annual Impact</p>
                    <p className="text-gray-700 leading-relaxed">
                      4 machines x 1,500 billable hrs/year = 6,000 hrs<br/>
                      Equipment rate: $85-$250/hr depending on machine<br/>
                      Average underbilling: 30 min/day per machine<br/>
                      Realistic recovery: <strong className="text-purple-700">$12,000/year</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-soft hover:shadow-large transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-start gap-4">
                <div className="bg-warning-100 p-2.5 rounded-xl flex-shrink-0">
                  <Clock className="w-5 h-5 text-warning-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg mb-2 text-gray-900">Disputed Hours & Client Friction</h4>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    "Your guy wasn't here until 9" — without GPS proof, you eat the cost or
                    lose the client. Every dispute erodes trust.
                  </p>
                  <div className="bg-warning-50/80 rounded-xl p-4 text-sm border border-warning-100">
                    <p className="font-semibold text-warning-800 mb-2 text-xs uppercase tracking-wider">Hidden Costs</p>
                    <p className="text-gray-700 leading-relaxed">
                      2-3 billing disputes per month<br/>
                      Average write-off per dispute: $400<br/>
                      30 disputes/year x $400 = <strong className="text-warning-700">$12,000/year</strong><br/>
                      Plus: time spent resolving disputes (~2 hrs each)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-soft hover:shadow-large transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-2.5 rounded-xl flex-shrink-0">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg mb-2 text-gray-900">Administrative Overhead</h4>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    Collecting paper timecards, entering data, cross-referencing equipment logs,
                    chasing down discrepancies — every single week.
                  </p>
                  <div className="bg-blue-50/80 rounded-xl p-4 text-sm border border-blue-100">
                    <p className="font-semibold text-blue-800 mb-2 text-xs uppercase tracking-wider">Time Breakdown</p>
                    <p className="text-gray-700 leading-relaxed">
                      Daily timecard collection & data entry: 45 min/day<br/>
                      Weekly discrepancy resolution: 2 hours<br/>
                      Client invoicing prep: 3 hours/week<br/>
                      <strong>Total: ~9 hours/week</strong><br/>
                      Conservative estimate: <strong className="text-blue-700">$8,000/year</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-large border border-danger-200">
            <h4 className="font-bold text-2xl mb-5 text-center text-gray-900">Total Annual Waste</h4>
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                <span className="text-gray-600">Time theft & rounding errors</span>
                <span className="font-bold text-xl text-danger-600 tabular-nums">$20,000</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                <span className="text-gray-600">Equipment billing errors</span>
                <span className="font-bold text-xl text-purple-600 tabular-nums">$12,000</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                <span className="text-gray-600">Disputed hours & write-offs</span>
                <span className="font-bold text-xl text-warning-600 tabular-nums">$12,000</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                <span className="text-gray-600">Administrative overhead</span>
                <span className="font-bold text-xl text-blue-600 tabular-nums">$8,000</span>
              </div>
              <div className="flex justify-between items-center py-4 bg-gradient-to-r from-danger-50 to-danger-100/60 rounded-xl px-5 mt-3">
                <span className="font-bold text-xl text-gray-900">TOTAL ANNUAL LOSS</span>
                <span className="font-extrabold text-3xl text-danger-600 tabular-nums">$52,000</span>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-5 text-center">
              * Based on a contractor with 4 machines and 10 operators at ~$28/hr average.
              Scales linearly with fleet size — a 10-machine operation loses $100K+/year.
            </p>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section id="how-it-works" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/80 text-primary-800 rounded-full text-sm font-semibold mb-5 border border-primary-200/60">
              <Zap className="w-4 h-4" />
              The Solution
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">How Foreman Works</h3>
            <p className="text-lg text-gray-600">Automated tracking from clock-in to invoice</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-primary-100 to-primary-200/60 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:shadow-glow-primary transition-all duration-300 group-hover:-translate-y-1">
                <Shield className="w-10 h-10 text-primary-700" />
              </div>
              <h4 className="font-bold text-xl mb-3 text-gray-900">1. RFID Clock In</h4>
              <p className="text-gray-600 leading-relaxed">
                Operators tap their RFID card on the equipment-mounted sensor. System validates
                certification and creates a timecard automatically.
              </p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-success-100 to-success-200/60 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:shadow-glow-success transition-all duration-300 group-hover:-translate-y-1">
                <MapPin className="w-10 h-10 text-success-700" />
              </div>
              <h4 className="font-bold text-xl mb-3 text-gray-900">2. Automatic Tracking</h4>
              <p className="text-gray-600 leading-relaxed">
                GPS + accelerometer track equipment location and movement in 15-minute increments.
                No manual entry, no disputes — just data.
              </p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-purple-100 to-purple-200/60 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 transition-all duration-300 group-hover:-translate-y-1">
                <DollarSign className="w-10 h-10 text-purple-700" />
              </div>
              <h4 className="font-bold text-xl mb-3 text-gray-900">3. Instant Billing</h4>
              <p className="text-gray-600 leading-relaxed">
                Dual-rate pay calculation (base + equipment multiplier) happens automatically.
                Generate project invoices with one click.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100/60 rounded-3xl p-8 md:p-10 border border-gray-200/80">
            <h4 className="font-bold text-2xl mb-8 text-gray-900 text-center tracking-tight">Technology Stack</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-soft hover:shadow-medium transition-all duration-300">
                <div className="bg-primary-100 p-2 rounded-lg flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-primary-700" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">TrackUnit Sensor</div>
                  <div className="text-sm text-gray-600">
                    LoRa mesh + GPS + 6-axis IMU + RFID reader mounted on each piece of equipment
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-soft hover:shadow-medium transition-all duration-300">
                <div className="bg-primary-100 p-2 rounded-lg flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-primary-700" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Movement Detection</div>
                  <div className="text-sm text-gray-600">
                    IMU accelerometer tracks active vs. idle time for accurate billing
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-soft hover:shadow-medium transition-all duration-300">
                <div className="bg-primary-100 p-2 rounded-lg flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-primary-700" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">SiteHub Gateway</div>
                  <div className="text-sm text-gray-600">
                    Touchscreen kiosk collects data via LoRa mesh — works without WiFi or cell service
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-soft hover:shadow-medium transition-all duration-300">
                <div className="bg-primary-100 p-2 rounded-lg flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-primary-700" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">LoRa Mesh Network</div>
                  <div className="text-sm text-gray-600">
                    Up to 2-mile range, multi-hop relay for remote job sites with no infrastructure
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation & Hardware */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-success-100/80 text-success-800 rounded-full text-sm font-semibold mb-5 border border-success-200/60">
              <Settings className="w-4 h-4" />
              Installation
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">100% Non-Invasive Installation</h3>
            <p className="text-lg text-gray-600">
              No wiring, no drilling, no equipment modifications — keeps warranties intact
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
            {/* Hardware Components */}
            <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-medium hover:shadow-large transition-all duration-300">
              <h4 className="font-bold text-2xl mb-7 text-gray-900 flex items-center gap-3">
                <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-2.5 rounded-xl shadow-md">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                TrackUnit Sensor
              </h4>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-50 p-2 rounded-lg mt-0.5 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">External Mounting Only</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      Magnetic base or industrial straps attach to equipment exterior.
                      No drilling, no holes, no permanent modifications.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary-50 p-2 rounded-lg mt-0.5 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">Weatherproof Enclosure</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      IP67-rated housing protects electronics from water, dust, mud, and impacts.
                      Designed for construction site conditions.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary-50 p-2 rounded-lg mt-0.5 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">Battery or 12V Power</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      Built-in rechargeable battery (30-day life) OR simple 12V tap from equipment battery.
                      No connection to vehicle electronics or CAN bus.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary-50 p-2 rounded-lg mt-0.5 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">RFID Reader Integration</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      Reader mounts near operator entrance. Employee taps RFID card to clock in/out.
                      Range: 3-6 inches (tap-to-activate, no accidental scans).
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Installation Process */}
            <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-medium hover:shadow-large transition-all duration-300">
              <h4 className="font-bold text-2xl mb-7 text-gray-900 flex items-center gap-3">
                <div className="bg-gradient-to-br from-success-500 to-success-700 p-2.5 rounded-xl shadow-md">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                15-Minute Installation
              </h4>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="bg-success-100 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-success-700 flex-shrink-0 text-sm">
                    1
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">Mount Sensor Unit</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      Attach to equipment roof, fender, or frame using magnetic base or heavy-duty straps.
                      Position for clear GPS signal and LoRa reception.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-success-100 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-success-700 flex-shrink-0 text-sm">
                    2
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">Position RFID Reader</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      Mount near cabin entrance or operator seat.
                      Cable routes from sensor unit to reader (included, weatherproof).
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-success-100 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-success-700 flex-shrink-0 text-sm">
                    3
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">Optional: 12V Power Connection</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      If using 12V power instead of battery, connect to equipment battery terminals.
                      Simple two-wire connection (+ and -). No integration with equipment systems.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-success-100 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-success-700 flex-shrink-0 text-sm">
                    4
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-0.5">Power On & Go</div>
                    <div className="text-sm text-gray-600 leading-relaxed">
                      Device auto-registers with the gateway. Register the equipment in the dashboard
                      and tracking begins immediately — no calibration needed.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-7 p-5 bg-gradient-to-r from-success-50 to-success-100/60 rounded-2xl border border-success-200/80">
                <div className="font-bold text-success-800 mb-1.5 flex items-center gap-2 text-sm">
                  <Shield className="w-4 h-4" />
                  Warranty Safe
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong className="text-gray-900">Zero impact on equipment warranties.</strong> No modifications to OEM systems,
                  electronics, or hydraulics. Fully removable if equipment is sold or leased out.
                </p>
              </div>
            </div>
          </div>

          {/* Technical Specs */}
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-200 shadow-medium">
            <h4 className="font-bold text-2xl mb-8 text-gray-900 text-center tracking-tight">Technical Specifications</h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="font-semibold text-gray-900 mb-3">Connectivity</div>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> LoRa 915MHz mesh network</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> GPS for location tracking</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> 6-axis IMU for motion detection</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> Range: up to 2 miles line-of-sight</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="font-semibold text-gray-900 mb-3">Power & Durability</div>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> Battery: 30-day rechargeable Li-ion</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> 12V input: 9-16V DC compatible</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> IP67 waterproof rating</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> Operating temp: -20°C to 60°C</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="font-semibold text-gray-900 mb-3">Dimensions & Weight</div>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> Sensor unit: 4" x 3" x 1.5"</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> Weight: 8 oz (with battery)</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> RFID reader: 2" x 2" x 0.5"</li>
                  <li className="flex items-start gap-2"><span className="text-primary-500 mt-1">&#8226;</span> Total system weight: &lt;1 lb</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center shadow-soft hover:shadow-medium transition-all duration-300">
              <div className="text-3xl md:text-4xl font-extrabold text-primary-600 mb-1 tracking-tight">15 min</div>
              <div className="text-sm font-medium text-gray-600">Installation per machine</div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center shadow-soft hover:shadow-medium transition-all duration-300">
              <div className="text-3xl md:text-4xl font-extrabold text-success-600 mb-1 tracking-tight">$0</div>
              <div className="text-sm font-medium text-gray-600">Equipment modifications</div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center shadow-soft hover:shadow-medium transition-all duration-300">
              <div className="text-3xl md:text-4xl font-extrabold text-purple-600 mb-1 tracking-tight">100%</div>
              <div className="text-sm font-medium text-gray-600">Removable & portable</div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center shadow-soft hover:shadow-medium transition-all duration-300">
              <div className="text-3xl md:text-4xl font-extrabold text-blue-600 mb-1 tracking-tight">0</div>
              <div className="text-sm font-medium text-gray-600">Warranty voids</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/80 text-primary-800 rounded-full text-sm font-semibold mb-5 border border-primary-200/60">
              <Activity className="w-4 h-4" />
              Features
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">Everything You Need</h3>
            <p className="text-lg text-gray-600">One system to replace all your manual processes</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-primary-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-primary-100/80 p-3 rounded-xl inline-block mb-4">
                <Wifi className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="font-bold text-lg mb-2 text-gray-900">Real-Time Monitoring</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                See equipment status, operator locations, and active timecards live from anywhere.
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-success-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-success-100/80 p-3 rounded-xl inline-block mb-4">
                <Clock className="w-6 h-6 text-success-600" />
              </div>
              <h4 className="font-bold text-lg mb-2 text-gray-900">15-Minute Billing Increments</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Movement-based tracking rounds to 15-minute increments for fair, accurate billing.
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-purple-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-purple-100/80 p-3 rounded-xl inline-block mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-bold text-lg mb-2 text-gray-900">Certification Validation</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Automatic checks ensure only certified operators use specialized equipment.
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-success-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-success-100/80 p-3 rounded-xl inline-block mb-4">
                <DollarSign className="w-6 h-6 text-success-600" />
              </div>
              <h4 className="font-bold text-lg mb-2 text-gray-900">Dual-Rate Pay Calculation</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Base wage + equipment multiplier for active hours. Calculated automatically.
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-blue-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-blue-100/80 p-3 rounded-xl inline-block mb-4">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-bold text-lg mb-2 text-gray-900">GPS Location Tracking</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Know exactly where your equipment is at all times. Prove job site presence to clients.
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-primary-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-primary-100/80 p-3 rounded-xl inline-block mb-4">
                <TrendingUp className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="font-bold text-lg mb-2 text-gray-900">Project Invoicing</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Assign equipment to projects, track budgets, and generate invoices with one click.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Line Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/80 text-primary-800 rounded-full text-sm font-semibold mb-5 border border-primary-200/60">
              <Radio className="w-4 h-4" />
              Product Family
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">The Foreman Product Line</h3>
            <p className="text-lg text-gray-600">Purpose-built hardware for every role on the job site</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {/* TrackUnit */}
            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-primary-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-primary-100/80 p-3 rounded-xl inline-block mb-4">
                <Truck className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="font-bold text-lg mb-1 text-gray-900">TrackUnit</h4>
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-3">Equipment Timecard</p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Equipment-mounted sensor with GPS, 6-axis IMU, LoRa mesh, and RFID reader.
                Automates timecards with 15-minute activity-based billing.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> GPS + motion tracking</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> RFID operator auth</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> 2-mile LoRa mesh</div>
              </div>
            </div>

            {/* ZerkLink */}
            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-success-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-success-100/80 p-3 rounded-xl inline-block mb-4">
                <Wrench className="w-6 h-6 text-success-600" />
              </div>
              <h4 className="font-bold text-lg mb-1 text-gray-900">ZerkLink</h4>
              <p className="text-xs font-semibold text-success-600 uppercase tracking-wider mb-3">Grease Verification</p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Handheld grease gun node that verifies every lube point via RFID tags and pressure sensing.
                Prevents $35K+ bearing failures with proof of service.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> RFID zerk point tags</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> Pressure verification</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> Clog/no-flow detection</div>
              </div>
            </div>

            {/* IronWatch */}
            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-purple-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-purple-100/80 p-3 rounded-xl inline-block mb-4">
                <Thermometer className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-bold text-lg mb-1 text-gray-900">IronWatch</h4>
              <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-3">Machine Health Monitor</p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Fixed telemetry node with PT100 turbine temperature, vibration RMS analysis,
                and threshold alerts. Enables predictive maintenance and accurate bidding.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> PT100 temp monitoring</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> Vibration RMS analysis</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> Overheating alerts</div>
              </div>
            </div>

            {/* SiteHub */}
            <div className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-blue-200 hover:shadow-large transition-all duration-300 hover:-translate-y-1">
              <div className="bg-blue-100/80 p-3 rounded-xl inline-block mb-4">
                <Wifi className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-bold text-lg mb-1 text-gray-900">SiteHub</h4>
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-3">Job Site Gateway</p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Touchscreen kiosk with LoRa bridge radio. Collects data from all nodes on site,
                runs the dashboard locally, and works without WiFi or cell service.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> Touchscreen kiosk</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> No internet required</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> Real-time dashboard</div>
              </div>
            </div>
          </div>

          {/* ZerkTag consumable callout */}
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-success-50 to-success-100/60 rounded-2xl p-6 border border-success-200/80">
            <div className="flex flex-col md:flex-row items-center gap-5">
              <div className="bg-white p-3 rounded-xl shadow-soft flex-shrink-0">
                <Tag className="w-8 h-8 text-success-600" />
              </div>
              <div className="text-center md:text-left">
                <h4 className="font-bold text-lg text-gray-900 mb-1">ZerkTag RFID Kits</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Industrial RFID tags for every grease point on your machines. Scan a zerk fitting, verify pressure,
                  get instant proof of service. <strong className="text-success-700">$49 per 25-tag kit</strong> — each tag rated for 10,000+ scans
                  in oil, mud, and extreme temperatures.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/80 text-primary-800 rounded-full text-sm font-semibold mb-5 border border-primary-200/60">
              <DollarSign className="w-4 h-4" />
              Pricing
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">Simple, Transparent Pricing</h3>
            <p className="text-lg text-gray-600">Pay for what you use — one gateway, plus per-machine hardware and service</p>
          </div>

          {/* Hardware Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto mb-10">
            <div className="group bg-white rounded-3xl p-7 border border-blue-200 shadow-medium hover:shadow-large transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gradient-to-br from-blue-100 to-blue-200/60 p-2.5 rounded-xl">
                  <Wifi className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900">SiteHub</h4>
                  <p className="text-xs text-gray-500">One per job site</p>
                </div>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight">$350</span>
                <span className="text-sm font-normal text-gray-400 ml-1">one-time</span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>Touchscreen kiosk</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>LoRa bridge (2-mile range)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>Pre-configured, plug & play</span>
                </li>
              </ul>
            </div>

            <div className="group bg-white rounded-3xl p-7 border border-primary-200 shadow-medium hover:shadow-large transition-all duration-300 hover:-translate-y-0.5 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs font-bold px-3 py-1 rounded-full">CORE</div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gradient-to-br from-primary-100 to-primary-200/60 p-2.5 rounded-xl">
                  <Truck className="w-6 h-6 text-primary-700" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900">TrackUnit</h4>
                  <p className="text-xs text-gray-500">One per machine</p>
                </div>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight">$219</span>
                <span className="text-sm font-normal text-gray-400 ml-1">per unit</span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>GPS + IMU + LoRa mesh</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>RFID reader + 5 cards</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>15-min install, no mods</span>
                </li>
              </ul>
            </div>

            <div className="group bg-white rounded-3xl p-7 border border-success-200 shadow-medium hover:shadow-large transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gradient-to-br from-success-100 to-success-200/60 p-2.5 rounded-xl">
                  <Wrench className="w-6 h-6 text-success-700" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900">ZerkLink</h4>
                  <p className="text-xs text-gray-500">Grease verification</p>
                </div>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight">$229</span>
                <span className="text-sm font-normal text-gray-400 ml-1">per unit</span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>Pressure transducer</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>RFID zerk point scanning</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>Clog/no-flow detection</span>
                </li>
              </ul>
            </div>

            <div className="group bg-white rounded-3xl p-7 border border-purple-200 shadow-medium hover:shadow-large transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gradient-to-br from-purple-100 to-purple-200/60 p-2.5 rounded-xl">
                  <Thermometer className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900">IronWatch</h4>
                  <p className="text-xs text-gray-500">Machine health</p>
                </div>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight">$249</span>
                <span className="text-sm font-normal text-gray-400 ml-1">per unit</span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>PT100 temp monitoring</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>Vibration RMS analysis</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
                  <span>Predictive alerts</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Monthly Service */}
          <div className="max-w-4xl mx-auto mb-10">
            <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 rounded-3xl p-8 md:p-10 text-white shadow-large relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
              <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h4 className="font-bold text-2xl mb-1.5 tracking-tight">Monthly Service</h4>
                  <p className="text-white/70 text-sm">Dashboard, firmware updates, project invoicing, support</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-extrabold tracking-tight">$29</div>
                  <div className="text-white/60 text-sm font-medium">per machine / month</div>
                </div>
              </div>
              <div className="relative mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-white/10 rounded-xl p-3.5 text-center backdrop-blur-sm">
                  <div className="font-bold">2 machines</div>
                  <div className="text-white/60 text-xs mt-0.5">$58/mo</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3.5 text-center backdrop-blur-sm">
                  <div className="font-bold">4 machines</div>
                  <div className="text-white/60 text-xs mt-0.5">$116/mo</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3.5 text-center backdrop-blur-sm">
                  <div className="font-bold">8 machines</div>
                  <div className="text-white/60 text-xs mt-0.5">$232/mo</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3.5 text-center backdrop-blur-sm">
                  <div className="font-bold">12 machines</div>
                  <div className="text-white/60 text-xs mt-0.5">$348/mo</div>
                </div>
              </div>
            </div>
          </div>

          {/* What's included */}
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                <span>Real-time dashboard + mobile access</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                <span>Project tracking & invoice generation</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                <span>Unlimited operators & RFID cards ($3/card)</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                <span>Firmware updates & remote support</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                <span>CSV & report export</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                <span>No long-term contracts — cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 md:p-12 shadow-large border border-gray-200">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-success-100/80 text-success-800 rounded-full text-sm font-semibold mb-5 border border-success-200/60">
                <TrendingUp className="w-4 h-4" />
                ROI Analysis
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 tracking-tight">Return on Investment</h3>
              <p className="text-lg text-gray-600">Example: 4 machines, 10 operators — here's the math</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white rounded-2xl p-7 border border-danger-100 shadow-soft">
                <h4 className="font-bold text-lg mb-5 text-danger-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Without Foreman (Annual)
                </h4>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">Time theft & rounding:</span>
                      <span className="font-bold tabular-nums">$20,000</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      5% overpayment on manual timecards
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">Equipment billing errors:</span>
                      <span className="font-bold tabular-nums">$12,000</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      Under/overbilling from inaccurate time logs
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">Disputes & write-offs:</span>
                      <span className="font-bold tabular-nums">$12,000</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      ~30 client disputes/year at ~$400 each
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">Admin overhead:</span>
                      <span className="font-bold tabular-nums">$8,000</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      ~9 hrs/week processing timecards
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">Missed maintenance failures:</span>
                      <span className="font-bold tabular-nums">$15,000</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      Unverified greasing, bearing failures, unplanned downtime
                    </div>
                  </div>
                  <div className="border-t-2 border-danger-100 pt-3 flex justify-between font-bold text-lg text-danger-600">
                    <span>Total Annual Waste:</span>
                    <span className="tabular-nums">$67,000</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-7 border border-success-100 shadow-soft">
                <h4 className="font-bold text-lg mb-5 text-success-600 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Foreman Costs (Year 1)
                </h4>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">SiteHub Gateway (one-time):</span>
                      <span className="font-bold tabular-nums">$350</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      Pi 5 + touchscreen + LoRa bridge
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">4 TrackUnit sensors (one-time):</span>
                      <span className="font-bold tabular-nums">$876</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      $219/unit x 4 machines, includes RFID
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 text-sm">Monthly service (12 months):</span>
                      <span className="font-bold tabular-nums">$1,392</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-3">
                      $29/machine x 4 machines = $116/mo
                    </div>
                  </div>
                  <div className="border-t-2 border-success-100 pt-3 flex justify-between font-bold text-lg text-success-600">
                    <span>First Year Total:</span>
                    <span className="tabular-nums">$2,618</span>
                  </div>
                  <div className="text-xs text-gray-500 pl-3">
                    Year 2+: $1,392/year (service only)
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 mb-8 border border-gray-200">
              <h4 className="font-bold text-lg mb-3 text-gray-900">The Math</h4>
              <div className="flex items-center gap-3 flex-wrap text-sm text-gray-700">
                <div className="font-mono bg-white px-4 py-2 rounded-xl border border-gray-200 font-bold text-gray-900">$67,000</div>
                <span className="text-gray-400 font-medium">-</span>
                <div className="font-mono bg-white px-4 py-2 rounded-xl border border-gray-200 font-bold text-gray-900">$2,618</div>
                <span className="text-gray-400 font-medium">=</span>
                <div className="font-mono bg-success-100 text-success-700 px-4 py-2 rounded-xl font-bold border border-success-200">$64,382 net savings</div>
              </div>
              <div className="text-xs text-gray-500 mt-3 pl-1">
                Payback period: $2,618 / ($67,000 / 12) = <strong className="text-success-700">~14 days (under 2 weeks)</strong>
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white rounded-2xl p-8 md:p-10 text-center shadow-large relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
              <div className="relative">
                <div className="text-5xl md:text-6xl font-extrabold mb-2 tracking-tight">$64,382</div>
                <div className="text-xl mb-3 font-medium text-white/90">Net Savings in Year One</div>
                <div className="text-sm text-white/60">System pays for itself in under 2 weeks</div>
              </div>
            </div>

            <div className="mt-6 p-5 bg-blue-50/80 rounded-2xl border border-blue-100">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong className="text-blue-800">Scales with your fleet:</strong> Adding a machine is just $219 hardware + $29/mo.
                A 10-machine operation saves $130K+/year for $3,480/year in service. Add ZerkLink
                ($229) or IronWatch ($249) to any site for preventive maintenance — a single prevented
                bearing failure ($35K+) pays for the entire product line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h3 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight">Ready to See It in Action?</h3>
          <p className="text-xl mb-10 text-white/70 max-w-2xl mx-auto leading-relaxed">
            Explore the live demo dashboard with real-time data and see how Foreman can transform
            your construction business.
          </p>
          <a
            href="/demo.html"
            className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-primary-700 text-xl font-bold rounded-2xl shadow-large hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300"
          >
            <Activity className="w-6 h-6" />
            Launch Live Demo
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-white/50">
            <span>Interactive demo</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Real-time updates</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>No signup required</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 py-10 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-1.5 rounded-lg">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">Foreman</span>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            IoT-based automated timecard and billing solution for construction equipment operators
          </p>
          <div className="w-12 h-px bg-gray-800 mx-auto mb-3" />
          <p className="text-xs text-gray-600">
            &copy; 2026 Foreman Systems
          </p>
        </div>
      </footer>
    </div>
  );
}
